import React, { useEffect, useState } from 'react';
import {
  HowToSaveType,
  Target,
  howToSaveInvestmentProducts,
} from 'components/features/target-achievement-support/utils/type';
import { targetTypeToTitle } from 'components/features/target-achievement-support/utils/targetInfo';
import { useTargetSupportRouting } from 'components/features/target-achievement-support/hooks/useTargetSupportRouting';
import { useTargetAchievementSupportContext } from 'contexts/TargetAchievementSupportContext';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useModal } from 'hooks/useModal';
import { formatDate } from 'utils/formatter';
import { completeTarget } from 'config/api';
import { PeriodTabs } from './components/PeriodTabs';
import { AllTime } from './components/AllTime';
import { Month } from './components/Month';
import { Year } from './components/Year';
import { HowToSaveCard } from './components/HowToSaveCard';
import { TargetInformationBasic } from './components/TargetInformationBasic';
import { PortfolioAnalysis } from './components/PortfolioAnalysis';
import { MonthlyTargetDecisionModal } from '../modal/MonthlyTargetDecisionModal';
import { EndTargetModal } from '../modal/EndTargetModal';
import { TargetAchievementRateModal } from '../modal/TargetAchievementRateModal';
import { TargetContinuationModal } from '../modal/TargetContinuationModal';
import { AdviceNotAchievedModal } from '../modal/AdviceNotAchievedModal';

type Props = {
  target: Target;
  birthday: Date;
  targetHowToSaveType: HowToSaveType;
};

export const isHowToSaveInvestmentTrust = (type: HowToSaveType) => {
  return type === howToSaveInvestmentProducts;
};

export const TargetInformation = ({
  target,
  birthday,
  targetHowToSaveType,
}: Props): React.ReactElement => {
  useCustomHeaderTitle('資産目標サポート');
  const {
    updateGlobalTarget,
    updateConfirmGlobalTarget,
    updateIsFirstFlg,
  } = useTargetAchievementSupportContext();

  const {
    goSetTitle,
    goHowToIncreaseAccumulationAmount,
    goTargetPushUpdateConfirm,
    goPortfolioAnalysis: goPortfolioAnalysis_,
    goTargetIndex,
  } = useTargetSupportRouting();

  const [period, setPeriod] = useState<
    React.ComponentProps<typeof PeriodTabs>['period']
  >('Month');
  const [endTargetId, setEndTargetId] = useState<string | null>(null);

  const [
    isShowMonthlyTargetDecisionModal,
    {
      hideModal: hideMonthlyTargetDecisionModal,
      showModal: showMonthlyTargetDecisionModal,
    },
  ] = useModal();

  const [
    isShowTargetAchievementRateModal,
    {
      hideModal: hideTargetAchievementRateModal,
      showModal: showTargetAchievementRateModal,
    },
  ] = useModal();

  const [
    isShowTargetContinuationModal,
    {
      hideModal: hideTargetContinuationModal,
      showModal: showTargetContinuationModal,
    },
  ] = useModal();

  const [
    isShowAdviceNotAchievedModal,
    {
      hideModal: hideAdviceNotAchievedModal,
      showModal: showAdviceNotAchievedModal,
    },
  ] = useModal();

  const [
    isShowEndTargetModal,
    { hideModal: hideEndTargetModal, showModal: showEndTargetModal_ },
  ] = useModal();

  const achievementRate = (target.assetAmount / target.targetAmount) * 100;
  const nowDate = new Date();
  const startDate = new Date(target.startDate);
  const keepPeriod =
    nowDate.getFullYear() * 12 +
    nowDate.getMonth() -
    (startDate.getFullYear() * 12 + startDate.getMonth());
  const registeredAccounts = target.accountList.map(list => {
    return list.brokerName;
  });

  const goUpdateTarget = () => {
    updateIsFirstFlg(true);
    updateGlobalTarget(null);
    updateConfirmGlobalTarget(null);
    goTargetPushUpdateConfirm(target.id, 'edit');
  };

  const goPortfolioAnalysis = () => {
    goPortfolioAnalysis_(target.id);
  };

  const endTarget = async (): Promise<void> => {
    if (endTargetId === null) return;
    await completeTargetAPI(endTargetId);
    goTargetIndex();
  };

  const showEndTargetModal = (id: string) => {
    setEndTargetId(id);
    showEndTargetModal_();
  };

  useEffect(() => {
    switch (target.status) {
      case 2:
      case 3:
        return showMonthlyTargetDecisionModal();
      case 4:
        return showAdviceNotAchievedModal();
      case 5:
        return showTargetAchievementRateModal();
      case 6:
        return showTargetContinuationModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Google tag manager
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('chrome') !== -1) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'target_detail',
        targetinfo: `${target.id}_${target.title}_${targetTypeToTitle(
          target.refType
        )}_${formatDate(new Date(), 'yyyyMMddHHmmss')}`,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div>
        <TargetInformationBasic
          target={target}
          birthday={birthday}
          isShowRemainDate
          updateTarget={() => goUpdateTarget()}
          editTitle={() => goSetTitle(target.id)}
          endTarget={() => showEndTargetModal(target.id)}
        />
        <div>
          <PeriodTabs {...{ period, setPeriod, canSelect: true }} />
          {((): React.ReactElement => {
            switch (period) {
              case 'AllTime':
                return (
                  <AllTime
                    target={target}
                    goHowToIncreaseAccumulationAmount={() =>
                      goHowToIncreaseAccumulationAmount(target.id)
                    }
                    targetHowToSaveType={targetHowToSaveType}
                  />
                );
              case 'Month':
                return (
                  <Month
                    target={target}
                    goHowToIncreaseAccumulationAmount={() =>
                      goHowToIncreaseAccumulationAmount(target.id)
                    }
                    targetHowToSaveType={targetHowToSaveType}
                  />
                );
              case 'Year':
                return (
                  <Year
                    target={target}
                    goHowToIncreaseAccumulationAmount={() =>
                      goHowToIncreaseAccumulationAmount(target.id)
                    }
                    targetHowToSaveType={targetHowToSaveType}
                  />
                );
            }
          })()}
        </div>
        <div className="pt-4">
          <HowToSaveCard
            target={target}
            targetHowToSaveType={targetHowToSaveType}
            birthday={birthday}
            className="mx-4"
            goTargetUpdateConfirm={() => goUpdateTarget()}
            registeredAccounts={registeredAccounts}
          />
        </div>
        <div>
          <div className="font-bold text-bluegray-700 text-lg mt-5 mx-4">
            ポートフォリオ分析
          </div>
          <PortfolioAnalysis
            {...{
              id: target.id,
              goPortfolioAnalysis,
              registeredAccounts,
              isShowGoToPortfolio: isHowToSaveInvestmentTrust(
                targetHowToSaveType
              ),
            }}
            className="mx-4 mt-2"
          />
        </div>
      </div>
      <EndTargetModal
        {...{
          endTarget,
          hide: hideEndTargetModal,
          isShow: isShowEndTargetModal,
        }}
      />
      {target.status === 2 ||
        (target.status === 3 && (
          <MonthlyTargetDecisionModal
            {...{
              isShow: isShowMonthlyTargetDecisionModal,
              hide: hideMonthlyTargetDecisionModal,
              id: target.id,
              title: target.title,
              type: target.refType,
              status: target.status,
            }}
          />
        ))}
      {target.status === 5 && achievementRate >= 10 && (
        <TargetAchievementRateModal
          {...{
            isShow: isShowTargetAchievementRateModal,
            hide: hideTargetAchievementRateModal,
            title: target.title,
            startDate: new Date(target.startDate),
            targetAmount: target.targetAmount,
            accumulationAmount: target.assetAmount,
            percentage: achievementRate,
          }}
        />
      )}
      {target.status === 6 && keepPeriod > 0 && (
        <TargetContinuationModal
          {...{
            isShow: isShowTargetContinuationModal,
            hide: hideTargetContinuationModal,
            keepPeriod: keepPeriod,
          }}
        />
      )}
      <AdviceNotAchievedModal
        {...{
          isShow: isShowAdviceNotAchievedModal,
          hide: hideAdviceNotAchievedModal,
          type: targetHowToSaveType,
          goPortfolioAnalysis,
        }}
      />
    </>
  );
};

const completeTargetAPI = async (id: string): Promise<void> => {
  await completeTarget(id);
};
